import { Button } from 'blaze-ui/Button'
import Styles from './InsightChannelsSelection.module.scss'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { FooterButtons } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Chip } from 'components/Chip/Chip'
import { useState } from 'react'
import cx from 'classnames'
import { blogChannels, emailChannels, organicChannels } from './utils'

export interface InsightChannelsSelectionProps {
  onBack: () => void
  onNext: (insightChannels: string[]) => void
  initialInsightChannels: string[]
}

export const InsightChannelsSelection = ({ onBack, onNext, initialInsightChannels }: InsightChannelsSelectionProps) => {
  const [selectedChannels, setSelectedChannels] = useState<string[]>(initialInsightChannels)

  const handleChannelSelection = (channelName: string) => {
    if (selectedChannels.includes(channelName)) {
      setSelectedChannels(selectedChannels.filter((channel) => channel !== channelName))
    } else {
      setSelectedChannels([...selectedChannels, channelName])
    }
  }

  return (
    <MultiStepStepLayout
      title="Where do you currently post?"
      description="Tell us your active channels so we can create optimized content and track performance"
      scrollContainerClassName={Styles.scrollContainer}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                Back
              </Button>
              <Button
                size="lg"
                variant="primary"
                isDisabled={!selectedChannels.length}
                onPress={() => onNext(selectedChannels)}
                aria-label="Skip for now"
              >
                Continue
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.container}>
          <div className={Styles.category}>
            <Heading level={4}>Organic:</Heading>
            <div className={Styles.channels}>
              {organicChannels.map((channel) => (
                <Chip
                  size="xxl"
                  icon={channel.icon}
                  key={channel.name}
                  onPress={() => handleChannelSelection(channel.name)}
                  variant="flat"
                  selectable
                  isSelected={selectedChannels.includes(channel.name)}
                  deletable={false}
                  className={cx(Styles.channel, { [Styles.selected]: selectedChannels.includes(channel.name) })}
                >
                  {channel.title}
                </Chip>
              ))}
            </div>
          </div>
          <div className={Styles.category}>
            <Heading level={4}>Blog:</Heading>
            <div className={Styles.channels}>
              {blogChannels.map((channel) => (
                <Chip
                  size="xxl"
                  icon={channel.icon}
                  key={channel.name}
                  onPress={() => {
                    const allBlogChannelNames = blogChannels.map((c) => c.name)
                    const isCurrentlySelected = selectedChannels.includes(channel.name)

                    if (isCurrentlySelected) {
                      // Deselect this channel
                      setSelectedChannels(selectedChannels.filter((ch) => ch !== channel.name))
                    } else {
                      // Remove all blog channels, then add this one
                      setSelectedChannels([
                        ...selectedChannels.filter((ch) => !allBlogChannelNames.includes(ch)),
                        channel.name,
                      ])
                    }
                  }}
                  variant="flat"
                  selectable
                  isSelected={selectedChannels.includes(channel.name)}
                  deletable={false}
                >
                  {channel.title}
                </Chip>
              ))}
            </div>
          </div>
          <div className={Styles.category}>
            <Heading level={4}>Email:</Heading>
            <div className={Styles.channels}>
              {emailChannels.map((channel) => (
                <Chip
                  size="xxl"
                  icon={channel.icon}
                  key={channel.name}
                  onPress={() => {
                    const allEmailChannelNames = emailChannels.map((c) => c.name)
                    const isCurrentlySelected = selectedChannels.includes(channel.name)

                    if (isCurrentlySelected) {
                      // Deselect this channel
                      setSelectedChannels(selectedChannels.filter((ch) => ch !== channel.name))
                    } else {
                      // Remove all email channels, then add this one
                      setSelectedChannels([
                        ...selectedChannels.filter((ch) => !allEmailChannelNames.includes(ch)),
                        channel.name,
                      ])
                    }
                  }}
                  variant="flat"
                  selectable
                  isSelected={selectedChannels.includes(channel.name)}
                  deletable={false}
                  className={cx(Styles.channel, { [Styles.selected]: selectedChannels.includes(channel.name) })}
                >
                  {channel.title}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
